import { AsyncState } from '..';
import { ActionReducerMapBuilder, AsyncThunk, PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import { VisitInfo } from 'src/services/auth';
import { AppointmentType, AutomedDoctorAvailabilityTimeModel, availabilityService } from 'src/services/availability';
import {
    AppointmentByHash,
    AutomedBookingRequestResponse,
    BookingServiceSource,
    BookingUserProfileModel,
    UmbracoClinicModel,
    bookingService,
} from 'src/services/booking';
import { MedicareCardStatus } from 'src/services/patient';
import { AutomedSiteModel, siteService } from 'src/services/site';
import { TelehealthAvailableModel } from 'src/services/telehealth';
import { queryStringOrSessionStorage, queryStringOrSessionStorageArray } from 'src/utils/queryStringOrSessionStorage';

type FetchClinicArgs = { siteId: string | number };
type FetchDoctorArgs = { siteId: string | number; doctorId: string | number };
type FetchAppointmentTypeArgs = { siteId: string | number; appointmentTypeId: string | number };
type CancelAppointmentArgs = { siteId: string | number; appointmentId: string | number; isPendingCancel?: boolean };

export type AppointmentState = {
    bookingServiceSource?: BookingServiceSource;
    dateTime: string;
    timeSlot: AutomedDoctorAvailabilityTimeModel;
    doctorErpId: number;
    clinicId: number;
    appointmentTypeId: number | string;
    /** @deprecated use bookingFlow.visitInfo.isNewPatient instead */
    isNewPatient: boolean;
    reservedBooking?: AutomedBookingRequestResponse;
    clinic: AsyncState<FetchClinicArgs, UmbracoClinicModel & AutomedSiteModel>;
    doctor: AsyncState<FetchDoctorArgs, BookingUserProfileModel>;
    appointmentType: AsyncState<FetchAppointmentTypeArgs, AppointmentType>;
    payment_id?: number;
    timeslotId?: number;
    reason?: string;
    visitInfo?: VisitInfo;
    medicareExemptions?: string[];
    medicareStatus?: MedicareCardStatus;
    appointmentComment?: string;
};

let isFetchingClinic: FetchClinicArgs | undefined = undefined;
export const fetchClinic = createAsyncThunk('appointment/fetchClinic', async (args: FetchClinicArgs) => {
    if (args.siteId && !isEqual(isFetchingClinic, args)) {
        isFetchingClinic = args;
        const [umbracoDetails, automedDetails] = await Promise.all([
            bookingService.GetClinicUmbracoDetails(args.siteId),
            siteService.GetAutomedDetails(args.siteId),
        ]);
        return { ...umbracoDetails, ...automedDetails } as UmbracoClinicModel & AutomedSiteModel;
    }
});

let isFetchingDoctor: FetchDoctorArgs | undefined = undefined;
export const fetchDoctor = createAsyncThunk('appointment/fetchDoctor', async (args: FetchDoctorArgs) => {
    if (args.siteId && args.doctorId && !isEqual(isFetchingDoctor, args)) {
        isFetchingDoctor = args;
        return await bookingService.GetDoctorDetails(args.siteId, args.doctorId);
    }
});

let isFetchingAppt: FetchAppointmentTypeArgs | undefined = undefined;
export const fetchAppointmentType = createAsyncThunk(
    'appointment/fetchAppointmentType',
    async (args: FetchAppointmentTypeArgs) => {
        if (args.siteId && !isEqual(args, isFetchingAppt)) {
            isFetchingAppt = args;
            return await availabilityService.GetAppointmentTypes(args.siteId);
        }
    }
);
export const cancelAppointment = createAsyncThunk(
    'appointment/cancelAppointment',
    async ({ siteId, appointmentId, isPendingCancel }: CancelAppointmentArgs) =>
        await bookingService.CancelBooking(siteId, appointmentId, isPendingCancel)
);

const reservedBooking = Object.keys(sessionStorage).reduce((createBookingResponse, key) => {
    const [a, b] = key.split('.');
    if (a === 'reservedBooking' && b) {
        createBookingResponse[b as keyof AutomedBookingRequestResponse] = JSON.parse(sessionStorage.getItem(key)!);
    }
    return createBookingResponse;
}, {} as Partial<AutomedBookingRequestResponse>);

const initialState: AppointmentState = {
    bookingServiceSource: queryStringOrSessionStorage('bookingService') as BookingServiceSource | undefined,
    dateTime: queryStringOrSessionStorage('dateTime')!,
    timeSlot: {
        duration: queryStringOrSessionStorage('timeSlot.duration')!,
        time: queryStringOrSessionStorage('timeSlot.time')!,
        ab: queryStringOrSessionStorage('timeSlot.ab'),
        r1: queryStringOrSessionStorage('timeSlot.r1'),
        r2: queryStringOrSessionStorage('timeSlot.r2'),
        r3: queryStringOrSessionStorage('timeSlot.r3'),
        r4: queryStringOrSessionStorage('timeSlot.r4'),
    },
    doctorErpId: Number(queryStringOrSessionStorage('doctor')),
    clinicId: Number(queryStringOrSessionStorage('clinic')),
    appointmentTypeId: queryStringOrSessionStorage('appointment')!,
    isNewPatient: queryStringOrSessionStorage('new')! === 'true',
    reservedBooking:
        'appointment_id' in reservedBooking ? (reservedBooking as AutomedBookingRequestResponse) : undefined,
    clinic: { isLoading: false },
    doctor: { isLoading: false },
    appointmentType: { isLoading: false },
    payment_id: Number(sessionStorage.getItem('payment_id')) || undefined,
    reason: sessionStorage.getItem('appointment_reason') || undefined,
    timeslotId: Number(queryStringOrSessionStorage('timeslotId')) || undefined,
    medicareStatus: queryStringOrSessionStorage('medicareStatus') as MedicareCardStatus | undefined,
    medicareExemptions: queryStringOrSessionStorageArray('telehealthMedicareExemptions'),
    appointmentComment: queryStringOrSessionStorage('comment'),
};

function addCases<TArgs, TData, TReturn = TData>(
    builder: ActionReducerMapBuilder<AppointmentState>,
    key: keyof AppointmentState,
    request: AsyncThunk<TData, TArgs, object>,
    transform?: (payload: TData, args: TArgs) => TReturn,
    furtherFulfilled?: (state: AppointmentState) => void
) {
    builder.addCase(request.pending, (state, action) => {
        (state[key as keyof typeof state] as AsyncState<TArgs, TReturn>) = {
            isLoading: true,
            request: action.meta.arg,
        };
    });
    builder.addCase(request.fulfilled, (state, action) => {
        (state[key as keyof typeof state] as AsyncState<TArgs, TReturn>) = {
            data: transform ? transform(action.payload, action.meta.arg) : (action.payload as unknown as TReturn),
            isLoading: false,
            request: action.meta.arg,
        };
        furtherFulfilled?.(state);
    });
    builder.addCase(request.rejected, (state, action) => {
        (state[key as keyof typeof state] as AsyncState<TArgs, TReturn>) = {
            isLoading: false,
            error: action.error,
            request: action.meta.arg,
        };
    });
}

export const appointmentSlice = createSlice({
    name: 'appointment',
    initialState: initialState,
    reducers: {
        setBookingServiceSource: (state, action: PayloadAction<BookingServiceSource>) => {
            state.bookingServiceSource = action.payload;
            sessionStorage.setItem(`bookingService`, action.payload);
        },
        setClinicId: (state, action: PayloadAction<number>) => {
            state.clinicId = action.payload;
        },
        changeTime: (state, action: PayloadAction<{ date: Date; timeSlot: AutomedDoctorAvailabilityTimeModel }>) => {
            state.dateTime = action.payload.date.toISOString();
            sessionStorage.setItem('dateTime', action.payload.date.toISOString());
            state.timeSlot = action.payload.timeSlot;
            for (const [key, value] of Object.entries(action.payload.timeSlot)) {
                if (value) {
                    sessionStorage.setItem(`timeSlot.${key}`, value);
                } else {
                    sessionStorage.removeItem(`timeSlot.${key}`);
                }
            }
        },
        setReservedBooking: (state, action: PayloadAction<AutomedBookingRequestResponse>) => {
            state.reservedBooking = action.payload;
            for (const [key, value] of Object.entries(action.payload)) {
                sessionStorage.setItem(`reservedBooking.${key}`, JSON.stringify(value));
            }
        },
        removeReservedBooking: (state, _action: PayloadAction<void>) => {
            if (state.reservedBooking) {
                for (const [key, value] of Object.entries(state.reservedBooking)) {
                    sessionStorage.setItem(`reservedBooking.${key}`, JSON.stringify(value));
                }
            }
            state.reservedBooking = undefined;
        },
        setAppointmentTypeId: (state, action: PayloadAction<string>) => {
            state.appointmentTypeId = action.payload;
        },
        setAppointmentFromHash: (state, action: PayloadAction<AppointmentByHash>) => {
            state.appointmentTypeId = action.payload.appointment_type_id;
            state.dateTime = moment(`${action.payload.date} ${action.payload.time}`).toISOString();
            state.doctorErpId = action.payload.user_id;
        },
        setPaymentId: (state, action: PayloadAction<number>) => {
            state.payment_id = action.payload;
            sessionStorage.setItem('payment_id', action.payload.toString());
        },
        setReason: (state, action: PayloadAction<string>) => {
            state.reason = action.payload;
            sessionStorage.setItem('appointment_reason', action.payload);
        },
        setAppointmentFromTelehealthSlot: (
            state,
            {
                payload: { appointmentTypeId, siteId, slotId, slot, userId, slotTime, duration },
            }: PayloadAction<TelehealthAvailableModel>
        ) => {
            state.clinicId = siteId;
            sessionStorage.setItem('clinic', siteId.toString());

            state.doctorErpId = userId;
            sessionStorage.setItem('doctor', userId.toString());

            state.appointmentTypeId = appointmentTypeId;
            sessionStorage.setItem('appointment', appointmentTypeId.toString());

            state.timeslotId = slotId;
            sessionStorage.setItem('timeslotId', appointmentTypeId.toString());

            // TODO put timeslot date in slot
            const dateTime = moment(slot);
            state.dateTime = dateTime.toISOString();
            sessionStorage.setItem('dateTime', dateTime.toISOString());
            state.timeSlot = {
                time: slotTime,
                duration: duration.toString(),
            };
            sessionStorage.setItem('timeSlot.time', slotTime);
            sessionStorage.setItem('timeSlot.duration', duration.toString());
        },
    },
    extraReducers(builder) {
        addCases(builder, 'clinic', fetchClinic, undefined, () => (isFetchingClinic = undefined));
        addCases(builder, 'doctor', fetchDoctor, undefined, () => (isFetchingDoctor = undefined));
        addCases(
            builder,
            'appointmentType',
            fetchAppointmentType,
            (payload, args) => payload?.find((a) => a.appointmentTypeId === `${args.appointmentTypeId}`),
            () => (isFetchingAppt = undefined)
        );

        builder.addCase(cancelAppointment.fulfilled, (state) => {
            if (state.reservedBooking) {
                for (const [key] of Object.entries(state.reservedBooking!)) {
                    sessionStorage.removeItem(`reservedBooking.${key}`);
                }
                state.reservedBooking = undefined;
            }
        });
        builder.addCase(cancelAppointment.rejected, (state, request) => {
            if (request.error.message === 'Appointment not found or cancelled.') {
                for (const [key] of Object.entries(state.reservedBooking!)) {
                    sessionStorage.removeItem(`reservedBooking.${key}`);
                }
                state.reservedBooking = undefined;
            }
        });
    },
});

// Action creators are generated for each case reducer function
export const {
    changeTime,
    setBookingServiceSource,
    setClinicId,
    setAppointmentFromHash,
    setAppointmentTypeId,
    removeReservedBooking,
    setPaymentId,
    setReason,
    setAppointmentFromTelehealthSlot,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
